import * as React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { AuthBrandingCard } from '../AuthBrandingCard';
import { AuthCarouselControls } from './AuthCarouselControls';
import { AuthChatPreviewCard } from './AuthChatPreviewCard';

const AUTO_PLAY_INTERVAL = 6000; // 6 seconds

interface AuthCarouselProps {
  hasUtm: boolean;
  utmEcho: string | null;
}

export const AuthCarousel: React.FC<AuthCarouselProps> = ({ hasUtm, utmEcho }) => {
  const [activeSlide, setActiveSlide] = React.useState(hasUtm ? 1 : 0);

  const timerRef = React.useRef<number | null>(null);

  // Define slides array
  const slides = React.useMemo(
    () => [
      { id: 'branding', component: <AuthBrandingCard /> },
      {
        id: 'chat-background',
        component: <AuthChatPreviewCard hasUtm={hasUtm} utmEcho={utmEcho} />,
      },
    ],
    [hasUtm, utmEcho]
  );

  const totalSlides = slides.length;

  // Auto-play logic (disabled when UTM — locked to chat preview slide)
  const startTimer = React.useCallback(() => {
    if (hasUtm) return;
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
    }
    // window.setInterval returns number; the global setInterval is typed as NodeJS.Timeout.
    timerRef.current = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % totalSlides);
    }, AUTO_PLAY_INTERVAL);
  }, [totalSlides, hasUtm]);

  React.useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [startTimer]);

  // Manual interaction handler (resets timer)
  const handleManualChange = (index: number) => {
    setActiveSlide(index);
    startTimer(); // Reset the timer so it doesn't jump immediately after a click
  };

  return (
    <div
      className={clsx(
        'relative hidden h-full shrink-0 lg:block',
        // Default Wide width
        'w-[50.76%]',
        // 4xl: 744px / 1553px ≈ 47.9% width
        '4xl:w-[47.9%]'
      )}
    >
      {/* 
        Viewport / Mask
        Strict styling to match parent rounding and ensure no overflow 
      */}
      <div className='size-full overflow-hidden rounded-[35px] bg-brand-970'>
        {/* Sliding Track */}
        <motion.div
          className='flex size-full'
          animate={{ x: `${-activeSlide * 100}%` }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30, // Smooth spring, not bouncy
            mass: 1,
          }}
        >
          {slides.map((slide) => (
            <div
              key={slide.id}
              className='size-full min-w-full shrink-0'
              // Important: min-w-full ensures each slide takes full width of the viewport
            >
              {slide.component}
            </div>
          ))}
        </motion.div>
      </div>

      {!hasUtm && (
        <AuthCarouselControls
          activeSlide={activeSlide}
          onSlideChange={handleManualChange}
          totalSlides={totalSlides}
        />
      )}
    </div>
  );
};
